import React from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import { ROUTES } from "config/navConfig";

const VEHICLE_LABELS = {
  "limited-partnership": "Limited Partnership",
  "llp": "Limited Liability Partnership",
  "private-limited": "Private Limited Company",
  "unit-trust": "Unit Trust",
};

export default function ConfirmationScreen({ data }) {
  const navigate = useNavigate();
  const targetSize = parseFloat(data?.targetSize) || 0;
  const investors = data?.investors || [];
  const totalCommitted = investors?.reduce((s, i) => s + (parseFloat(i?.commitment) || 0), 0);

  return (
    <div className="flex flex-col items-center text-center py-8 lg:py-12 px-4">
      {/* Success Icon */}
      <div
        className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
        style={{ background: "rgba(5,150,105,0.1)" }}
      >
        <Icon name="CheckCircle2" size={40} color="var(--color-success)" />
      </div>
      <h2 className="font-heading text-2xl lg:text-3xl font-semibold text-foreground mb-2">
        SPV Created Successfully
      </h2>
      <p className="font-caption text-sm text-muted-foreground max-w-md mb-8">
        Your Special Purpose Vehicle has been structured and saved as a draft. Review the summary below and proceed to the SPV dashboard to manage commitments and documents.
      </p>
      {/* SPV Summary Card */}
      <div
        className="w-full max-w-lg rounded-xl border p-6 text-left mb-8"
        style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}
      >
        <div className="flex items-start gap-4 mb-6">
          <div
            className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(59,76,76,0.1)" }}
          >
            <Icon name="Briefcase" size={22} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-semibold text-foreground">
              {data?.name || "Unnamed SPV"}
            </h3>
            <p className="font-caption text-sm text-muted-foreground">
              {VEHICLE_LABELS?.[data?.vehicleType] || "SPV"} · {data?.jurisdiction || "—"}
            </p>
          </div>
          <div className="ml-auto flex-shrink-0">
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-caption font-semibold"
              style={{ background: "rgba(217,119,6,0.1)", color: "var(--color-warning)" }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--color-warning)" }} />
              Draft
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {[
            { label: "Target Size", value: targetSize > 0 ? `£${targetSize?.toLocaleString("en-GB")}` : "—", icon: "Target" },
            { label: "Committed", value: totalCommitted > 0 ? `£${totalCommitted?.toLocaleString("en-GB")}` : "—", icon: "Users" },
            { label: "Management Fee", value: data?.managementFee ? `${data?.managementFee}% p.a.` : "—", icon: "Percent" },
            { label: "Carried Interest", value: data?.carry ? `${data?.carry}%` : "—", icon: "TrendingUp" },
            { label: "Final Close", value: data?.finalCloseDate ? new Date(data.finalCloseDate)?.toLocaleDateString("en-GB") : "—", icon: "Calendar" },
            { label: "Tokenisation", value: data?.tokenisationEnabled ? "Enabled" : "Not Enabled", icon: "Coins" },
          ]?.map((item) => (
            <div key={item?.label} className="flex items-center gap-2.5">
              <div
                className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
                style={{ background: "var(--color-muted)" }}
              >
                <Icon name={item?.icon} size={14} color="var(--color-muted-foreground)" />
              </div>
              <div>
                <p className="font-caption text-xs text-muted-foreground">{item?.label}</p>
                <p className="font-caption text-sm font-semibold text-foreground">{item?.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Next Steps */}
      <div className="w-full max-w-lg mb-8">
        <h4 className="font-caption font-semibold text-sm text-foreground mb-3 text-left">Recommended Next Steps</h4>
        <div className="space-y-2">
          {[
            { icon: "FolderOpen", label: "Upload legal documents to the Data Room", action: "data-room" },
            { icon: "Users", label: "Send commitment letters to investors", action: null },
            { icon: "FileCheck", label: "Complete KYC/AML verification for all investors", action: null },
            { icon: "BarChart3", label: "Configure ESG reporting parameters", action: "esg-reporting" },
          ]?.map((step, idx) => (
            <div
              key={idx}
              className="flex items-center gap-3 p-3 rounded-lg border"
              style={{ borderColor: "var(--color-border)", background: "var(--color-muted)" }}
            >
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: "rgba(59,76,76,0.1)" }}
              >
                <Icon name={step?.icon} size={14} color="var(--color-primary)" />
              </div>
              <span className="font-caption text-sm text-foreground flex-1 text-left">{step?.label}</span>
              {step?.action && (
                <Icon name="ChevronRight" size={14} color="var(--color-muted-foreground)" />
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Actions */}
      <div className="flex flex-col sm:flex-row items-center gap-3 w-full max-w-lg">
        <Button
          variant="default"
          iconName="LayoutDashboard"
          iconPosition="left"
          iconSize={16}
          onClick={() => navigate("/spv-dashboard")}
          fullWidth
        >
          Go to SPV Dashboard
        </Button>
        <Button
          variant="outline"
          iconName="Search"
          iconPosition="left"
          iconSize={16}
          onClick={() => navigate(ROUTES.spvDashboard)}
          fullWidth
        >
          Browse Opportunities
        </Button>
      </div>
    </div>
  );
}